import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { DefaultButton, IButtonProps, Label, PrimaryButton, Spinner, SpinnerSize, Stack } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../../shared/logging";
import { validateProps } from "../../../shared/props";
import { Icon } from "../../Icon/Icon";
import { NoHrefLink, NoHrefLinkProps } from "../../Links/NoHrefLink/NoHrefLink";
import { OnClickButtonProps, TextButtonProps } from "../shared/props";
import { getAltButtonStyles, getButtonStyles, getErrorButtonStyles } from "./Button.styles";

export const buttonTypes = [
    "default",
    "primary",
    "primaryAlt",
    "error"
] as const;
export type ButtonType = typeof buttonTypes[number];

interface ButtonTypeProps {
    /**
     * Type of button to render. Defaults to DefaultButton
     */
    readonly type?: ButtonType;

    readonly operationData?: ButtonOperationData;
}

export interface ButtonOperationData {
    /**
     * Operation information needed for states of Button.
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Props for error action when an operation failed.
     */
    readonly errorButton?: ErrorButtonProps;
}

export interface ErrorButtonProps {
    /**
     * Error message when operation failed.
     */
    readonly errorMessage: string;

    /**
     * Error message aria label when operation failed.
     */
    readonly errorMessageAriaLabel: string;

    /**
     * The functional handler for retrying.
     */
    readonly retryLink: NoHrefLinkProps;
}

export type ButtonProps = TextButtonProps & OnClickButtonProps & ButtonTypeProps;

const ErrorButton: React.FC<ErrorButtonProps> = ({
    errorMessage,
    errorMessageAriaLabel,
    retryLink,
    children,
    ...otherProps
}) => {
    validateProps("ErrorButton", otherProps);

    return (
        <Stack horizontal={true} tokens={{ childrenGap: 8 }} style={{ display: "inline-flex" }}>
            <Stack tokens={{ padding: "5px 0 0 0" }}>
                <Icon iconName={"Error"} color="errorIcon" size="12px" />
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 6 }}>
                <Label aria-label={errorMessageAriaLabel}>{errorMessage}</Label>
                <NoHrefLink {...retryLink} />
            </Stack>
        </Stack>
    );
};

export const Button: React.FC<ButtonProps> = ({
    type = "default",
    onClick,
    text,
    "aria-label": ariaLabel,
    disabled = false,
    allowDisabledFocus = false,
    operationData,
    ...otherProps
}) => {
    useComponentLogger("Button", otherProps.id);

    validateProps("Button", otherProps);

    const theme = useTheme();

    const style =
        type === "primaryAlt" ? // If type is primary alt
            getAltButtonStyles(theme) : //then getAltButtonStyles
        type === "error" ? // else if type is error
            getErrorButtonStyles(theme) : // then getErrorButtonStyles
        getButtonStyles(); // else getButtonStyles

    const sharedProps: IButtonProps = {
        onClick,
        text,
        disabled,
        ariaLabel,
        allowDisabledFocus,
        styles: style,
        ...otherProps
    };

    const ComputedButton = type === "primary" || type === "primaryAlt" ? PrimaryButton : DefaultButton;

    if (operationData) {
        const errorView = operationData.errorButton ?
            <ErrorButton {...operationData.errorButton} /> : <ComputedButton {...sharedProps} />;

        const updatingButtonProps = {
            ...sharedProps,
            onClick: undefined,
            text,
            disabled: true,
            allowDisabledFocus: false,
            onRenderIcon: () => <Spinner size={SpinnerSize.small} styles={{ root: { marginRight: "4px" } }} />
        };

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                updatingView={<ComputedButton {...updatingButtonProps} />}
                finishedView={<ComputedButton {...sharedProps} />}
                failedView={errorView}
            />
        );
    } else {
        return (<ComputedButton {...sharedProps} />);
    }

};
